import * as Sentry from "@sentry/react";
import {
  EmailAuthProvider,
  getAuth,
  PhoneMultiFactorInfo,
  reauthenticateWithCredential,
  updatePassword
} from "firebase/auth";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Check, X } from "lucide-react";

import { ListCard, ListRow } from "@/components/ui/list-card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { validatePassword } from "../../helpers/string_helpers";
import { lawyerSelectors } from "../../redux/lawyer/selectors";
import { useMultiFactorAuth } from "../../hooks/useMultiFactorAuth";
import { MFAManagement } from "../MFAManagement";
import { ReauthenticationModal } from "../common/ReauthenticationModal";
import {
  EditActions,
  SettingsEditDialog,
  SettingsField,
  SettingsListRow
} from "./settingsParts";

const PASSWORD_RULES = [
  ["hasMinLength", "At least 8 characters"],
  ["hasLowercase", "At least 1 lowercase letter"],
  ["hasUppercase", "At least 1 uppercase letter"],
  ["hasNumber", "At least 1 number"],
  ["hasSpecial", "At least 1 special character"]
] as const;

type SecurityEditKey = "password" | "mfa" | null;

export function SecurityTab() {
  const email = useSelector(lawyerSelectors.selectEmail);

  const auth = getAuth();
  const user = auth.currentUser;

  const { enrolledFactors } = useMultiFactorAuth();
  const mfaEnabled = enrolledFactors.length > 0;
  const mfaFactorLabel = useMemo(() => {
    if (!mfaEnabled) return "Off";
    const phones = enrolledFactors
      .map((f) => (f as PhoneMultiFactorInfo).phoneNumber)
      .filter(Boolean) as string[];
    const first = phones[0];
    if (!first) return "On";
    const extra = Math.max(0, phones.length - 1);
    return extra > 0 ? `${first} (+${extra} more)` : first;
  }, [enrolledFactors, mfaEnabled]);

  const [editKey, setEditKey] = useState<SecurityEditKey>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const [draftPassword, setDraftPassword] = useState("");
  const [pendingPassword, setPendingPassword] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [reauthError, setReauthError] = useState("");

  const closeEdit = () => {
    setEditKey(null);
    setEditError("");
    setDraftPassword("");
  };

  const openEdit = (k: Exclude<SecurityEditKey, null>) => {
    setEditError("");
    setDraftPassword("");
    setEditKey(k);
  };

  const handleUpdatePassword = async (
    newPassword: string,
    currentPass: string
  ) => {
    if (!user) {
      setReauthError("No authenticated user found.");
      return;
    }
    setIsLoading(true);
    try {
      const authCredential = EmailAuthProvider.credential(email, currentPass);
      await reauthenticateWithCredential(user, authCredential);
      await updatePassword(user, newPassword);
      window.location.reload();
    } catch (e: any) {
      const errorCode = e?.code;
      if (errorCode === "auth/wrong-password") {
        setReauthError("Incorrect current password");
      } else if (errorCode === "auth/requires-recent-login") {
        setReauthError("Recent login required");
      } else {
        setReauthError("Authentication failed. Please try again.");
      }
      Sentry.captureException(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReauthentication = async () => {
    if (!pendingPassword || !currentPassword.trim()) {
      setReauthError("Please enter your current password");
      return;
    }

    setReauthError("");
    setIsLoading(true);
    try {
      await handleUpdatePassword(pendingPassword, currentPassword);
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelReauthentication = () => {
    setCurrentPassword("");
    setReauthError("");
    setPendingPassword(null);
  };

  const { requirements } = validatePassword(draftPassword);

  return (
    <div className="flex w-full flex-col gap-6 px-6 pt-4 pb-8">
      <ListCard>
        <SettingsListRow
          label="Password"
          value="••••••••"
          onClick={() => openEdit("password")}
        />
        <ListRow
          title="Multi-Factor Authentication (MFA)"
          description={
            <span className="text-sm text-fg-3">
              {mfaEnabled ? `Text Message · ${mfaFactorLabel}` : mfaFactorLabel}
            </span>
          }
          control={
            <Switch
              aria-label="Multi-Factor Authentication"
              checked={mfaEnabled}
              onCheckedChange={() => openEdit("mfa")}
            />
          }
        />
      </ListCard>

      <SettingsEditDialog
        open={editKey !== null}
        title={
          editKey === "password"
            ? "Change Password"
            : "Multi-Factor Authentication (MFA)"
        }
        error={editError}
        onClose={closeEdit}
      >
        {editKey === "password" ? (
          <>
            <SettingsField
              label="New Password"
              value={draftPassword}
              onChange={(e) => setDraftPassword(e.target.value)}
              placeholder="New password"
              type="password"
              autoComplete="new-password"
            />
            {draftPassword && !validatePassword(draftPassword).isValid ? (
              <div className="flex flex-col gap-1.5 text-sm">
                <p className="font-medium text-fg-2">Password Requirements</p>
                <ul className="flex flex-col gap-1">
                  {PASSWORD_RULES.map(([key, label]) => {
                    const met = requirements[key];
                    const Icon = met ? Check : X;
                    return (
                      <li
                        key={key}
                        className={cn(
                          "flex items-center gap-1.5",
                          met ? "text-success" : "text-danger"
                        )}
                      >
                        <Icon className="size-3.5" />
                        {label}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <EditActions
              saving={isLoading}
              saveLabel="Continue"
              onCancel={closeEdit}
              onSave={() => {
                if (!draftPassword) {
                  setEditError("Password is required.");
                  return;
                }
                if (!validatePassword(draftPassword).isValid) {
                  setEditError("Password does not meet requirements.");
                  return;
                }
                setPendingPassword(draftPassword);
                closeEdit();
                setCurrentPassword("");
                setReauthError("");
              }}
            />
          </>
        ) : null}
        {editKey === "mfa" ? <MFAManagement /> : null}
      </SettingsEditDialog>

      <ReauthenticationModal
        isOpen={pendingPassword !== null}
        onClose={cancelReauthentication}
        currentPassword={currentPassword}
        setCurrentPassword={setCurrentPassword}
        onConfirm={handleReauthentication}
        reauthError={reauthError}
        isLoading={isLoading}
      />
    </div>
  );
}
